// Chemin: C:\gestion-copro\src\pages\public\ConfirmationPage.tsx
// Page de confirmation après soumission
// Affiche le numéro de signalement et les prochaines étapes

import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { getSignalement } from '../../services/signalement.service';
import { Signalement, LOCALISATION_LABELS, URGENCE_LABELS, URGENCE_COLORS } from '../../types/signalement.types';

interface LocationState {
  numeroSignalement?: string;
}

const ConfirmationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const state = location.state as LocationState | null;
  
  const [signalement, setSignalement] = useState<Signalement | null>(null);
  const [loading, setLoading] = useState(true);
  const numeroSignalement = state?.numeroSignalement;

  useEffect(() => {
    const loadSignalement = async () => {
      if (id) {
        const data = await getSignalement(id);
        setSignalement(data);
      }
      setLoading(false);
    };

    loadSignalement();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center">
            <h1 className="text-2xl font-bold text-blue-900">
              🛰️ ORION PILOT
            </h1>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Message de succès */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-green-100 p-3">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-green-800 mb-2">
            Signalement enregistré !
          </h2>
          <p className="text-green-700">
            Votre signalement a bien été pris en compte.
          </p>
        </div>

        {/* Numéro de signalement */}
        {numeroSignalement && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8 text-center">
            <p className="text-sm text-gray-600 mb-2">Numéro de référence</p>
            <p className="text-3xl font-mono font-bold text-blue-900">
              {numeroSignalement}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Conservez ce numéro pour le suivi de votre signalement.
            </p>
          </div>
        )}

        {/* Récapitulatif du signalement */}
        {signalement && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Récapitulatif de votre signalement
            </h3>
            
            <dl className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:justify-between">
                <dt className="text-sm text-gray-500">Titre</dt>
                <dd className="text-sm font-medium text-gray-900">{signalement.titre}</dd>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:justify-between">
                <dt className="text-sm text-gray-500">Localisation</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {LOCALISATION_LABELS[signalement.localisation]} — {signalement.localisationDetail}
                </dd>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:justify-between">
                <dt className="text-sm text-gray-500">Catégorie</dt>
                <dd className="text-sm font-medium text-gray-900">{signalement.categorie}</dd>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center">
                <dt className="text-sm text-gray-500">Urgence</dt>
                <dd>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${URGENCE_COLORS[signalement.urgence]}`}>
                    {URGENCE_LABELS[signalement.urgence]}
                  </span>
                </dd>
              </div>
              
              {signalement.photos.length > 0 && (
                <div>
                  <dt className="text-sm text-gray-500 mb-2">Photos jointes</dt>
                  <dd className="flex gap-2 flex-wrap">
                    {signalement.photos.map((url, index) => (
                      <img
                        key={index}
                        src={url}
                        alt={`Photo ${index + 1}`}
                        className="h-16 w-16 object-cover rounded-lg border border-gray-200"
                      />
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* Prochaines étapes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Que va-t-il se passer maintenant ?
          </h3>
          
          <ol className="space-y-4">
            <li className="flex">
              <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-800 text-sm font-bold mr-3">
                1
              </span>
              <div>
                <p className="font-medium text-gray-900">Réception</p>
                <p className="text-sm text-gray-600">
                  Le conseil syndical prend connaissance de votre signalement.
                </p>
              </div>
            </li>
            
            <li className="flex">
              <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-800 text-sm font-bold mr-3">
                2
              </span>
              <div>
                <p className="font-medium text-gray-900">Analyse</p>
                <p className="text-sm text-gray-600">
                  Le problème est évalué et catégorisé (parties communes, GPA...).
                </p>
              </div>
            </li>
            
            <li className="flex">
              <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-800 text-sm font-bold mr-3">
                3
              </span>
              <div>
                <p className="font-medium text-gray-900">Transmission</p>
                <p className="text-sm text-gray-600">
                  Selon la nature du problème, il est transmis au syndic ou au promoteur.
                </p>
              </div>
            </li>
            
            <li className="flex">
              <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-800 text-sm font-bold mr-3">
                4
              </span>
              <div>
                <p className="font-medium text-gray-900">Suivi</p>
                <p className="text-sm text-gray-600">
                  Vous serez tenu informé de l'avancement si vous avez fourni vos coordonnées.
                </p>
              </div>
            </li>
          </ol>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/signaler"
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Faire un autre signalement
          </Link>
        </div>

        {/* Note GPA */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                Rappel important
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                La garantie de parfait achèvement (GPA) de la résidence expire en 
                <strong> septembre 2026</strong>. N'hésitez pas à signaler tout problème 
                constaté dans les parties communes ou privatives avant cette date.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>
            Conseil Syndical L'ORION • Vos données sont protégées
          </p>
        </div>
      </main>
    </div>
  );
};

export default ConfirmationPage;
